/* yojo-cat-shelf.js — homepage inline category shelf (Release 3, 2026-08-02).
 *
 * Owner behavior ruling: a NORMAL activation of a category tile reveals that
 * category's products + vendor sub-choices inline; native navigation stays
 * intact for modified clicks, context menu, open-in-new-tab, no-JS, fetch
 * failure and timeout. Back/Forward restore the selection. Only the six
 * canonical registry handles (i.e. the tiles the registry itself rendered)
 * may hydrate from URL state.
 *
 * Trust boundary: the injected markup comes from THIS storefront's Section
 * Rendering API (same-origin, first-party Liquid we author) — the same source
 * the theme's facets and cart-drawer updates already parse. The only value
 * that ever reaches a URL is a handle validated against the registry-rendered
 * tile list; no user-supplied string is interpolated into markup here.
 *
 * Lifecycle contract (Outcome B): one AbortController owns every listener;
 * init is idempotent; destroy() is real; shopify:section:load re-inits.
 * Injection notifies the shared systems via yojo:content-added +
 * yojo:cards:added — rail controls, motion and quick add are all
 * document-delegated and need no per-node rebinding.
 */
(function () {
  'use strict';

  /* urlCat: the `cat` query value of the history entry the shelf last saw —
     set on init, on every pushState below and on every popstate it acts on.
     onPop() compares against it to recognise fragment-only history steps. */
  var STATE = { ac: null, fetchAc: null, current: '', shelf: null, strip: null, urlCat: '' };
  var TIMEOUT_MS = 6000;

  function $(sel, root) { return (root || document).querySelector(sel); }

  function catFromUrl() {
    return new URLSearchParams(location.search).get('cat') || '';
  }

  /* Rewrite ONLY the `cat` key, preserving every other query parameter.
   * Replacing the whole query string dropped utm_*, fbclid/gclid and
   * preview_theme_id on the first tile click — silent attribution loss on
   * live ad traffic, and a copied preview URL that no longer previews. */
  function urlWithCat(handle) {
    var p = new URLSearchParams(location.search);
    if (handle) p.set('cat', handle);
    else p.delete('cat');
    var q = p.toString();
    return location.pathname + (q ? '?' + q : '') + location.hash;
  }

  function tiles() {
    /* 2026-08-06 (owner: catalog behaves like the homepage doors): shelf
       tiles are the strip's .yhcs__tile OR any element opting in with
       [data-yhcs-tile] — the catalog directory's category tiles carry the
       attribute so they gain the inline shelf without inheriting the
       strip's tile styling. */
    return STATE.strip ? Array.prototype.slice.call(STATE.strip.querySelectorAll('.yhcs__tile, [data-yhcs-tile]')) : [];
  }

  function handleFromTile(a) {
    var m = (a.getAttribute('href') || '').match(/\/collections\/([a-z0-9-]+)/);
    return m ? m[1] : '';
  }

  function knownHandles() {
    return tiles().map(handleFromTile).filter(Boolean);
  }

  /* A product shown in both a page rail and the shelf yields two elements with
   * the same Shopify-generated id (`product_form_<id>`) — a WCAG 4.1.1
   * violation created by injection, invisible to any check that only runs on
   * initial load. Ids that already exist in the document are suffixed inside
   * the fragment, and every in-fragment reference to them is repointed, so the
   * markup stays internally consistent. */
  var ID_REF_ATTRS = ['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'aria-owns'];
  function dedupeIds(frag) {
    var n = 0;
    frag.querySelectorAll('[id]').forEach(function (el) {
      var id = el.id;
      if (!id || !document.getElementById(id)) return;
      var next = id + '--s' + (++n);
      while (document.getElementById(next) || frag.querySelector('#' + CSS.escape(next))) next += 'x';
      ID_REF_ATTRS.forEach(function (attr) {
        frag.querySelectorAll('[' + attr + '="' + CSS.escape(id) + '"]').forEach(function (ref) {
          ref.setAttribute(attr, next);
        });
      });
      el.id = next;
    });
  }

  function setStatus(text) {
    var s = $('[data-yhcs-status]');
    if (s) s.textContent = text;
  }

  function markSelected(handle) {
    tiles().forEach(function (a) {
      var on = handleFromTile(a) === handle;
      if (on) a.setAttribute('aria-current', 'true');
      else a.removeAttribute('aria-current');
      a.setAttribute('aria-expanded', on ? 'true' : 'false');
      a.classList.toggle('is-on', on);
    });
  }

  function closeShelf(fromPop) {
    if (STATE.fetchAc) { STATE.fetchAc.abort(); STATE.fetchAc = null; }
    if (STATE.shelf) {
      STATE.shelf.hidden = true;
      STATE.shelf.innerHTML = '';
      STATE.shelf.classList.remove('is-loading');
      STATE.shelf.style.minHeight = '';
    }
    markSelected('');
    STATE.current = '';
    setStatus('');
    if (!fromPop) {
      try { history.pushState({ yhcsCat: '' }, '', urlWithCat('')); STATE.urlCat = ''; } catch (e) { /* ignore */ }
    }
  }

  function openShelf(handle, fromPop) {
    if (!STATE.shelf) return;
    if (STATE.current === handle && !STATE.shelf.hidden) return;
    // Rapid switching: cancel the stale request before starting the next.
    if (STATE.fetchAc) STATE.fetchAc.abort();
    var ac = new AbortController();
    STATE.fetchAc = ac;
    STATE.current = handle;

    markSelected(handle);
    STATE.shelf.hidden = false;
    // Reserved, stable loading geometry — no CLS while the shelf resolves.
    // 2026-08-05 audit P1: the CSS is-loading floor is 320px but a real
    // mobile shelf measures 692-1248px, so a swap (door change / back-nav
    // rehydrate) collapsed then re-grew the page — measured CLS 0.31 on the
    // back path. Reserve the OUTGOING shelf's real height for the duration
    // of the load; the incoming content then replaces like-for-like.
    var outgoingH = STATE.shelf.offsetHeight;
    if (outgoingH > 0) STATE.shelf.style.minHeight = outgoingH + 'px';
    STATE.shelf.classList.add('is-loading');
    STATE.shelf.innerHTML = '<div class="yhcs-shelf__skeleton" aria-hidden="true"></div>';
    setStatus('טוען קטגוריה…');

    var timer = setTimeout(function () { ac.abort(); }, TIMEOUT_MS);
    // The Section Rendering API only renders sections that belong to the
    // REQUESTED template, so the shelf has its own alternate collection
    // template (templates/collection.catshelf.json) whose single section id
    // is `yojo-cat-shelf` — that id is the JSON response key.
    // NB: no `Accept: application/json` header — a collection URL answers that
    // header with the COLLECTION resource, shadowing the Section Rendering
    // API response entirely.
    fetch('/collections/' + handle + '?view=catshelf&sections=yojo-cat-shelf', { signal: ac.signal })
      .then(function (r) { if (!r.ok) throw new Error('shelf-fetch-' + r.status); return r.json(); })
      .then(function (json) {
        clearTimeout(timer);
        if (STATE.fetchAc !== ac) return; // a newer selection won
        var html = json['yojo-cat-shelf'] || '';
        // The section returns nothing for a non-canonical handle by design.
        var tpl = document.createElement('template');
        tpl.innerHTML = html;
        var content = tpl.content.querySelector('[data-yhcs-shelf-content]');
        if (!content) throw new Error('shelf-empty');
        dedupeIds(content);
        STATE.shelf.classList.remove('is-loading');
        STATE.shelf.style.minHeight = '';
        STATE.shelf.innerHTML = '';
        STATE.shelf.appendChild(content);
        var label = content.getAttribute('data-shelf-label') || '';
        var count = content.getAttribute('data-shelf-count') || '';
        setStatus('מציג ' + label + (count ? ', ' + count + ' מוצרים' : ''));
        if (!fromPop) {
          try { history.pushState({ yhcsCat: handle }, '', urlWithCat(handle)); STATE.urlCat = handle; } catch (e) { /* ignore */ }
        }
        document.dispatchEvent(new CustomEvent('yojo:content-added', { detail: { root: STATE.shelf } }));
        document.dispatchEvent(new CustomEvent('yojo:cards:added', { detail: { container: STATE.shelf } }));
      })
      .catch(function (err) {
        clearTimeout(timer);
        if (ac.signal.aborted && STATE.fetchAc !== ac) return; // superseded — the newer request owns the UI
        // Unrecoverable (timeout / HTTP error / empty): honor the native link.
        if (STATE.current === handle) {
          setStatus('');
          window.location.href = '/collections/' + handle;
        }
      });
  }

  function onClick(e) {
    var a = e.target && e.target.closest ? e.target.closest('.yhcs__tile, [data-yhcs-tile]') : null;
    if (!a || !STATE.strip || !STATE.strip.contains(a)) return;
    // Native behaviors stay native: modified clicks, middle click.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var handle = handleFromTile(a);
    if (!handle) return;
    /* A6-02 (2026-09-03): on a coarse pointer (touch), the 2026-08-04
       "first tap selects, second tap navigates" ruling below means a first
       tap can never leave the page — there is no hover affordance to show a
       touch user the door is now "selected but not navigated", so the tap
       reads as inert. Re-scoped to pointer devices only: a coarse/no-hover
       pointer follows the tile's own href immediately (native navigation,
       nothing prevented here); desktop keeps the inline-shelf ruling as-is. */
    if (window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches) return;
    /* Owner ruling 2026-08-04 (night), superseding the D-04 no-op: tapping the
       ALREADY-ACTIVE door follows the tile's own href to the category page —
       first tap selects, second tap navigates. Works identically on touch and
       mouse because it is just an un-prevented anchor click; the shelf still
       never collapses into an empty band (nothing closes here). Desktop only
       as of A6-02 above — see that comment for the coarse-pointer carve-out. */
    if (STATE.current === handle && STATE.shelf && !STATE.shelf.hidden) return;
    e.preventDefault();
    openShelf(handle, false);
  }

  /* Escape closes the disclosure and returns focus to the tile that opened it
   * — standard disclosure behaviour, and the shelf is otherwise only
   * dismissible by re-activating the same tile.
   *
   * 2026-09-26 (audit website-az-2026-09-25, runner home-a11y-speed #1): only
   * an Escape pressed INSIDE the strip or the shelf is ours. The listener sits
   * on document and the default shelf is open on arrival (D-04), so the
   * unscoped version treated every Escape on the homepage as "close the
   * shelf": closing the newsletter popup, the accessibility widget or search
   * also emptied the shelf (18 product links → 0) and tile.focus() threw the
   * page from y≈47 to ≈1588 onto the ארטיקים door. Scoped this way, focus only
   * moves (and the page only scrolls) when the customer was already in the
   * strip. An Escape another component consumed (defaultPrevented) is theirs. */
  function onKeydown(e) {
    if (e.key !== 'Escape' || e.defaultPrevented || !STATE.current) return;
    if (!STATE.shelf || STATE.shelf.hidden || !STATE.strip) return;
    var t = e.target;
    if (!t || !t.nodeType || !(STATE.strip.contains(t) || STATE.shelf.contains(t))) return;
    var handle = STATE.current;
    var tile = tiles().filter(function (a) { return handleFromTile(a) === handle; })[0];
    closeShelf(false);
    if (tile) tile.focus();
  }

  /* Back/Forward restore the selection — for history steps that CHANGE it.
   * The browser also fires popstate for fragment-only steps: the skip link
   * (#main), any in-page anchor, and Back/Forward across one. Those carry a
   * null state and the same ?cat=, and until 2026-09-26 they fell through to
   * closeShelf() — the skip link emptied the default shelf (audit
   * website-az-2026-09-25, runner home-a11y-speed #1). Entries this file
   * pushed carry `yhcsCat`; any other entry only counts when its ?cat=
   * differs from the one the shelf last saw. */
  function onPop(e) {
    var ours = !!(e.state && typeof e.state === 'object' && 'yhcsCat' in e.state);
    var cat = catFromUrl();
    if (!ours && cat === STATE.urlCat) return;
    STATE.urlCat = cat;
    var want = (e.state && e.state.yhcsCat) || cat || '';
    // Only canonical handles — i.e. the handles the registry itself rendered
    // as tiles — may hydrate from history/URL state.
    if (want && knownHandles().indexOf(want) !== -1) openShelf(want, true);
    else closeShelf(true);
  }

  function destroy() {
    if (STATE.ac) { STATE.ac.abort(); STATE.ac = null; }
    if (STATE.fetchAc) { STATE.fetchAc.abort(); STATE.fetchAc = null; }
    STATE.current = '';
    STATE.shelf = null;
    STATE.strip = null;
  }

  function init() {
    var strip = $('[data-yhcs-track]');
    var shelf = $('[data-yhcs-shelf]');
    if (!strip || !shelf) { destroy(); return; }
    if (STATE.strip === strip && STATE.shelf === shelf && STATE.ac) return; // idempotent
    destroy();
    STATE.strip = strip.closest('.yhcs') || strip;
    STATE.shelf = shelf;
    STATE.ac = new AbortController();
    var sig = { signal: STATE.ac.signal };
    document.addEventListener('click', onClick, sig);
    document.addEventListener('keydown', onKeydown, sig);
    window.addEventListener('popstate', onPop, sig);
    // URL hydration on first paint — canonical handles only.
    var want = catFromUrl();
    STATE.urlCat = want;
    var known = knownHandles();
    /* 2026-08-05 CLS fix: the strip section now SERVER-RENDERS the default
       category's shelf (same markup, same one owner —
       snippets/yojo-cat-shelf-content.liquid), because the fetch-based
       default arrived ~750ms after load and its 692px growth was the
       homepage's entire 0.097 CLS. Adopt what the server already drew
       instead of re-fetching it; an explicit ?cat= naming a DIFFERENT
       category still wins below (openShelf replaces the innerHTML). */
    var pre = shelf.querySelector('[data-yhcs-shelf-content]');
    if (pre) {
      var preHandle = pre.getAttribute('data-shelf-handle') || '';
      if (!want || want === preHandle) {
        STATE.current = preHandle;
        shelf.hidden = false;
        markSelected(preHandle);
        var preLabel = pre.getAttribute('data-shelf-label') || '';
        var preCount = pre.getAttribute('data-shelf-count') || '';
        setStatus('מציג ' + preLabel + (preCount ? ', ' + preCount + ' מוצרים' : ''));
        return;
      }
    }
    if (want && known.indexOf(want) !== -1) {
      openShelf(want, true);
    } else if (known.length && STATE.strip && STATE.strip.hasAttribute('data-yhcs-autoopen')) {
      /* D-04 auto-open is a HOMEPAGE ruling. Surfaces opt in with
         data-yhcs-autoopen on the track; the catalog directory
         (2026-08-06 owner ruling) waits for the click. */
      /* R4 D-04 (owner ruling): a category is ALWAYS open on arrival, and its
         products are visible without the customer clicking anything. Before
         this, nothing was active on load — the shelf stayed hidden and the
         category section read as an empty band until a tile was clicked.

         The default is the FIRST canonical destination in the registry, which
         is Popsicles under the owner's order. Deliberately not a hardcoded
         'popsicles' string: knownHandles() reads the rendered tiles, which are
         emitted in registry order, so re-ordering the registry re-points the
         default automatically and no hand-authored handle list appears here
         (C-19).

         fromPop:true is passed on purpose — the default open must NOT push a
         history entry or rewrite the URL. A customer arriving at "/" and
         pressing Back must leave the site, not step through a state they never
         chose. An explicit ?cat= link still wins over the default. */
      openShelf(known[0], true);
    }
  }

  document.addEventListener('shopify:section:load', function () { init(); });
  document.addEventListener('shopify:section:unload', function () { init(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
